import { useEffect, useState } from "react";
import type { AuditLogEntry, ManagedUser } from "@platform/contracts";
import { api } from "../api";
import { Modal } from "./Modal";
import type { NotificationKind } from "./Portal";
import { useLiveData } from "../useLiveData";

function isoDate(date: Date): string { return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`; }

export function AuditLogPanel({ users, onMessage }: { users: ManagedUser[]; onMessage: (message: string, kind?: NotificationKind) => void }) {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [actorId, setActorId] = useState("");
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [from, setFrom] = useState(() => isoDate(new Date(Date.now() - 6 * 86400000)));
  const [to, setTo] = useState(() => isoDate(new Date()));
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<AuditLogEntry | null>(null);
  const report = (error: Error) => onMessage(error.message, "error");

  const load = async () => {
    setLoading(true);
    try {
      const result = await api.auditLogs({ actorId: actorId || undefined, action: action || undefined, entityType: entityType || undefined, from, to, search: search.trim() || undefined, limit });
      setEntries(result.entries); setActions(result.actions); setEntityTypes(result.entityTypes); setHasMore(result.hasMore);
    } finally { setLoading(false); }
  };
  useEffect(() => { load().catch(report); }, [actorId, action, entityType, from, to, limit]);
  useLiveData(load);

  function reset() { setActorId(""); setAction(""); setEntityType(""); setSearch(""); setFrom(isoDate(new Date(Date.now() - 6 * 86400000))); setTo(isoDate(new Date())); setLimit(100); }
  const pretty = (value: string) => { try { const parsed = JSON.parse(value); return JSON.stringify(parsed, null, 2); } catch { return value; } };

  return <section className="audit-log-panel">
    <h2>System logs</h2>
    <p>Every recorded action across the platform. Logs are immutable and cannot be edited or deleted.</p>
    <div className="table-tools order-range-tools">
      <label className="range-field"><span>From</span><input type="date" value={from} onChange={(event) => setFrom(event.target.value)}/></label>
      <label className="range-field"><span>To</span><input type="date" value={to} onChange={(event) => setTo(event.target.value)}/></label>
      <select aria-label="Filter by user" value={actorId} onChange={(event) => setActorId(event.target.value)}><option value="">All users</option>{users.map((user) => <option key={user.id} value={user.id}>{user.displayName}</option>)}</select>
      <select aria-label="Filter by action" value={action} onChange={(event) => setAction(event.target.value)}><option value="">All actions</option>{actions.map((item) => <option key={item} value={item}>{item}</option>)}</select>
      <select aria-label="Filter by record type" value={entityType} onChange={(event) => setEntityType(event.target.value)}><option value="">All record types</option>{entityTypes.map((item) => <option key={item} value={item}>{item}</option>)}</select>
      <input value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") load().catch(report); }} placeholder="Search details or user, then Enter"/>
      <button className="secondary-button" disabled={loading} onClick={() => load().catch(report)}>{loading ? "Loading…" : "Search"}</button>
      <button className="secondary-button" onClick={reset}>Reset</button>
    </div>
    <div className="statement-summary">
      <div><small>Entries shown</small><strong>{entries.length}</strong></div>
      <div><small>Distinct actions</small><strong>{actions.length}</strong></div>
      <div><small>Record types</small><strong>{entityTypes.length}</strong></div>
    </div>
    <div className="data-table"><table>
      <thead><tr><th>When</th><th>User</th><th>Action</th><th>Record type</th><th>Record</th><th>Details</th></tr></thead>
      <tbody>{entries.map((entry) => <tr key={entry.id} tabIndex={0} className="editable-row" onClick={() => setSelected(entry)}>
        <td>{new Date(`${entry.occurredAt}Z`).toLocaleString()}</td>
        <td>{entry.actorName ? <><strong>{entry.actorName}</strong><small>{entry.actorRole?.replaceAll("_", " ")}</small></> : <span className="quick-action-empty">system / deleted user</span>}</td>
        <td><span className="doc-marker dn">{entry.action}</span></td>
        <td>{entry.entityType}</td>
        <td><small>{entry.entityId ?? "—"}</small></td>
        <td><small>{entry.metadata === "{}" ? "—" : `${entry.metadata.slice(0, 60)}${entry.metadata.length > 60 ? "…" : ""}`}</small></td>
      </tr>)}
      {entries.length === 0 && <tr><td colSpan={6} className="empty-cell">No log entries match these filters.</td></tr>}</tbody>
    </table></div>
    {hasMore && <div className="log-more"><span>Showing the most recent {entries.length} entries. Narrow the dates or load more.</span><button className="secondary-button" onClick={() => setLimit((current) => Math.min(current + 100, 500))}>Load more</button></div>}
    {selected && <Modal title={selected.action} onClose={() => setSelected(null)}>
      <div className="user-modal"><dl>
        <div><dt>When</dt><dd>{new Date(`${selected.occurredAt}Z`).toLocaleString()}</dd></div>
        <div><dt>User</dt><dd>{selected.actorName ? `${selected.actorName} (${selected.actorEmail})` : "System or deleted user"}</dd></div>
        <div><dt>Role</dt><dd>{selected.actorRole?.replaceAll("_", " ") ?? "—"}</dd></div>
        <div><dt>Record</dt><dd>{selected.entityType} · {selected.entityId ?? "—"}</dd></div>
      </dl>
      <div className="log-detail"><small>Details</small><pre>{pretty(selected.metadata)}</pre></div>
      </div>
    </Modal>}
  </section>;
}
